<template>
  <div
    class="wrap"
    :class="skeletonClass"
  >
    <div class="cover-block__breadcrumbs">
      <SkeletonBlock
        :is-active="isSkeletonActive"
        :width="128"
        :height="20"
      >
        <ToursBreadCrumbs
          v-if="!loaderStore.isFiltersLoading"
          :breadcrumbs
          :is-full-breadcrumbs="fullBreadcrumbsIsShown"
        />
      </SkeletonBlock>
    </div>
    <div class="title-block">
      <Text
        :skeleton="isSkeletonActive"
        as="h1"
        :text="seoTitle"
        class="regions-in-country-block__title"
        :skeleton-breakpoints="{ default: '70%', tablet: '70%', mobile: '320px' }"
      />
      <Text
        :skeleton="isSkeletonActive"
        as="h2"
        :text="seoSubtitle"
        class="regions-in-country-block__subtitle"
        :skeleton-breakpoints="'308px'"
      />
    </div>
    <TourRegionsInCountryCoverSkeleton v-if="isSkeletonActive" />

    <div
      v-else-if="regionsVisible.length > 0"
      class="regions-row"
      :style="countStyleStr"
    >
      <ToursRegionInCountryCard
        v-for="region in regionsVisible"
        :key="region.id"
        :region="region"
      />

      <span
        v-if="isVisibleMoreBtn"
        class="region-more"
        @click="openModal"
      >
        {{ t('regions_in_country.title') }}
        <ArrowRightIcon size="16" />
      </span>

      <ToursRegionsInCountryPopup
        v-if="isOpen"
        :isOpen="isOpen"
        :regions="regions"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { maxLength, useRegionsInCountryInfo } from '@/entities/regionsInCountry';
  import { ToursRegionInCountryCard } from '@/entities/regionsInCountry/ui/tour';
  import type { CountryInfo, CountryTag, RegionInfo, RegionsInCountryItems, RegionTag } from '@/shared/api/experience';
  import { ArrowRightIcon } from '@/shared/ui/icons/arrowRightIcon';
  import type { MappedRegionsInCountryInfo } from '@/entities/regionsInCountry/types/regionsInCountry';
  import { computed, nextTick, ref, watch, defineAsyncComponent } from 'vue';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import { Text } from '@/shared/ui/text';
  import TourRegionsInCountryCoverSkeleton from './TourRegionsInCountryCoverSkeleton.vue';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { generateTourBreadcrumbs, type GenerateBreadcrumbsParams } from '@/entities/breadCrumbs';
  import { ToursBreadCrumbs } from '@/entities/breadCrumbs/ui/tour';
  import { createRubricTitlesMap } from '@/entities/rubrics/utils/create-rubric-titles-map.ts';
  import { type FromCityPayload, LocationType } from '@/entities/location';
  import { useRoute } from 'vue-router';
  import { useCountryTagSeoInfo } from '@/entities/region/api/useCountryTagSeoInfo.ts';
  import { useTourSeoInfo } from '@/entities/region/api/useTourSeoInfo.ts';
  import { useHeaderZIndex } from '@/shared/composables/useHeaderZIndex.ts';
  import { useHead } from '@unhead/vue';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { useTourInCountryFromCitySeoInfo } from '@/entities/region/api/useTourInCountryFromCitySeoInfo.ts';
  import { useTagCanonicalLink } from '@/entities/tag/composables/useCanonicalLinks.ts';

  // Лениво подключает попап регионов только после открытия полного списка.
  const ToursRegionsInCountryPopup = defineAsyncComponent(
    () => import('@/entities/regionsInCountry/ui/tour/ToursRegionsInCountryPopup.vue'),
  );

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    currentTag: string;
    slug: string;
    page: number;
    fromCitySeoPayload?: Nullable<FromCityPayload>;
    geoObject: RegionInfo;
    rubric?: string | null;
    tags: RegionTag[] | CountryTag[];
  }
  const props = defineProps<Props>();

  const route = useRoute();

  const loaderStore = useLoaderStore();
  const seoStore = useSeoStore();

  const skeletonClass = computed(() => {
    return loaderStore.isFiltersLoading ? 'skeleton-active' : '';
  });

  const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();

  const breadcrumbs = computed(() => {
    const params: GenerateBreadcrumbsParams = {
      currentTag: props.currentTag,
      geoObject: props.geoObject,
      rubric: props.rubric ?? null,
      rubricTitlesMap: createRubricTitlesMap(props.tags),
      isExperienceDetailsPage: false,
      locationType: LocationType.COUNTRY,
    };

    params.country = route.params.countrySlug as string;
    params.countryName = (props.geoObject as CountryInfo)?.name_ru;

    return generateTourBreadcrumbs(params);
  });

  const fullBreadcrumbsIsShown = computed(() => props.currentTag !== 'all' && Boolean(props.currentTag));

  const isOpen = ref(false);
  const { isSkeletonActive } = useListingSkeleton();

  const { regionsInCountryInfo } = useRegionsInCountryInfo({
    country__slug: computed(() => props.slug),
  });

  const { tourSeoData: countrySeoData } = useTourSeoInfo(
    computed(() => 'country_tours'),
    computed(() => props.slug),
    computed(() => true),
  );

  const {
    countryTagSeoData,
    isSuccess: isCountryTourSeoLoaded,
    isFetching: isCountryTagSeoFetching,
  } = useCountryTagSeoInfo(
    computed(() => `${props.slug}__${props.currentTag}`),
    computed(() => props.currentTag !== 'all'),
  );

  const { tourInCountryFromCitySeoData } = useTourInCountryFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.countrySlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload),
  );

  const seoIsLoaded = computed(() => {
    if (props.currentTag === 'all' || props.currentTag === '') {
      return Boolean(countrySeoData.value?.seo);
    }

    return Boolean(isCountryTourSeoLoaded.value);
  });

  const seoIsFetching = computed(() => {
    if (props.currentTag === 'all' || props.currentTag === '') {
      return false;
    }

    return Boolean(isCountryTagSeoFetching.value);
  });

  const seoLoadStartTime = ref<number | null>(null);

  watch(
    () => props.slug,
    () => {
      seoLoadStartTime.value = null;
    },
  );

  const setSeoReady = () => {
    loaderStore.ready.seo = true;
    seoLoadStartTime.value = null;
  };

  const handleSeoLoaded = () => {
    if (seoLoadStartTime.value !== null) {
      const elapsed = Date.now() - seoLoadStartTime.value;
      const minDisplayTime = 800;
      const remainingTime = elapsed < 50 ? 0 : Math.max(0, minDisplayTime - elapsed);

      if (remainingTime > 0) {
        setTimeout(setSeoReady, remainingTime);
      } else {
        setSeoReady();
      }
    } else {
      setSeoReady();
    }
  };

  // Как в TourRegionCoverBlock: при смене рубрики сбрасываем SEO, чтобы isFiltersLoading включил скелетоны
  watch(
    () => props.currentTag,
    (newTag, oldTag) => {
      if (oldTag !== undefined && newTag !== oldTag) {
        loaderStore.ready.seo = false;
        seoLoadStartTime.value = Date.now();

        nextTick(() => {
          if (seoIsLoaded.value && !seoIsFetching.value && !loaderStore.ready.seo) {
            handleSeoLoaded();
          }
        });
      }
    },
  );

  watch(
    () => [seoIsLoaded.value, seoIsFetching.value],
    ([isLoaded, isFetching]) => {
      if (isFetching && !isLoaded) {
        if (loaderStore.ready.seo) {
          loaderStore.ready.seo = false;
        }

        return;
      }

      if (isLoaded && !isFetching && !loaderStore.ready.seo) {
        handleSeoLoaded();
      }
    },
    { immediate: true },
  );

  const regionsAdapter = (arr: RegionsInCountryItems[]) => {
    const newArr = [] as MappedRegionsInCountryInfo[];
    arr?.forEach((el: RegionsInCountryItems) => {
      const country = el?.countries?.[0]?.name_en?.toLowerCase() ?? '';
      const url = {
        name: 'regionListing',
        params: {
          country,
          regionSlug: el?.slug || '',
        },
      };

      newArr.push({
        id: el.id,
        url,
        name_ru: el.name || '',
        image: {
          thumbnail: el?.image?.thumbnail || el.cover_image_url,
          cover: el?.image?.cover || el?.image?.thumbnail || el.cover_image_url,
        },
      });
    });

    return newArr;
  };

  const regions = computed(() =>
    regionsInCountryInfo.value?.results?.length ? regionsAdapter(regionsInCountryInfo.value.results) : [],
  );
  const regionsVisible = computed(() => regions.value.slice(0, maxLength - 1));
  const isVisibleMoreBtn = computed(() => regions.value.length > maxLength);
  const count = computed(() => (isVisibleMoreBtn.value ? maxLength : regions.value.length));

  const countStyleStr = computed(() => `--count: ${count.value}`);
  const isAllRubricLabel = computed(() => {
    const locationTitle =
      props.currentTag === 'all' || props.currentTag === '' ? countrySeoData.value?.seo : countryTagSeoData.value?.seo;

    return !!props.fromCitySeoPayload ? tourInCountryFromCitySeoData.value?.seo : locationTitle;
  });
  const headTitle = computed(() => isAllRubricLabel.value?.title ?? '');
  const seoTitle = computed(() => isAllRubricLabel.value?.h1 || '');
  const seoSubtitle = computed(() => isAllRubricLabel.value?.h2 || '');
  const noindex = computed(() => isAllRubricLabel?.value?.noindex || false);

  watch(
    () => headTitle.value,
    (value) => (document.title = value),
    { immediate: true },
  );

  const openModal = () => {
    decreaseZIndex();
    isOpen.value = true;
  };
  const closeModal = () => {
    isOpen.value = false;
    increaseZIndex();
  };

  const description = computed(() => {
    if (route.query?.page && Number(route.query.page) > 1) {
      return '';
    }

    if (!!props.fromCitySeoPayload) {
      return isAllRubricLabel.value?.description || '';
    }

    if (props.currentTag === 'all' || props.currentTag === '') {
      return countrySeoData?.value?.seo?.description || isAllRubricLabel.value?.description || '';
    }

    return countryTagSeoData.value?.seo?.description || '';
  });

  const canonicalLink = useTagCanonicalLink(computed(() => props.currentTag));

  useHead({
    title: () => headTitle.value,
    link: [
      {
        rel: 'canonical',
        href: () => canonicalLink.value,
      },
    ],
    meta: () => [
      {
        name: 'description',
        content: () => description.value,
      },
      {
        property: 'og:title',
        content: () => headTitle.value,
      },
      {
        property: 'og:description',
        content: () => description.value,
      },
      {
        content: () => seoStore.resizeImage,
        property: 'og:image',
      },
      {
        property: 'og:url',
        content: () => canonicalLink.value,
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:image',
        content: () => seoStore.resizeImage,
      },
      {
        name: 'robots',
        content: noindex.value ? 'noindex, nofollow, max-image-preview:large' : 'max-image-preview:large',
      },
    ],
  });
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .skeleton-active {
    min-height: 264px;
  }

  .title-block {
    margin-bottom: 4px;
  }

  .regions-in-country-block__title {
    margin-top: 8px;
    text-wrap: balance;

    @include typography.base-1000-950-extra;

    @include adaptivity.tablet {
      @include typography.base-700-500-extra;
    }
  }

  .regions-in-country-block__subtitle {
    display: block;
    overflow: hidden;
    margin: 8px 0 0;
    text-overflow: ellipsis;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    @include typography.base-300-300-medium;
  }

  .regions-row {
    display: grid;
    grid-template: 1fr / repeat(var(--count), 140px);
    gap: 0 8px;
    overflow-x: scroll;
    margin: 32px -20px 12px;
    padding: 0 20px;

    &::-webkit-scrollbar {
      display: none;
    }

    @include adaptivity.tablet {
      margin: 24px -20px 12px;
    }
  }

  .region-more {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    max-width: 88px;
    max-height: 112px;
    padding: 12px;
    border: 1px solid var(--alias-color-border-subtle);
    border-radius: 8px;
    color: var(--alias-color-text-default);
    cursor: pointer;

    .icon {
      fill: var(--alias-color-icon-default);
    }
  }
</style>
